import { useId } from "react";
import Tooltip from "@web-ui/components/Tooltip";
import Icon from "@web-ui/components/Icon";

/*
 * Text field, restyled as the Tixora template's (HeroUI v3) text field: the label above, the
 * input, then the description or the error; hover, focus ring and invalid outline as HeroUI
 * draws them, with a 16px corner (owner, 2026-10-11). Styles in styles/controls.css.
 * `innerLabel` (the kit's floating label) renders as the label above, so existing screens
 * keep working. Variant "secondary" (default) is HeroUI's filled field for white cards;
 * "primary" is its white field with a soft shadow for grey surfaces.
 */
type FieldProps = {
    className?: string;
    classInput?: string;
    label?: string;
    innerLabel?: string;
    tooltip?: string;
    children?: React.ReactNode;
    textarea?: boolean;
    type?: string;
    validated?: boolean;
    handleForgotPassword?: () => void;
    forgotPasswordLabel?: string;
    /** Hint under the control. */
    description?: string;
    /** Validation message; replaces the description while shown. */
    error?: string;
    variant?: "primary" | "secondary";
    /** Marks the field as not required: the kit's optional word after the label (owner 2026-10-13). */
    optional?: boolean;
    /** The optional word, for apps that translate (English by default). */
    optionalLabel?: string;
    /** md is the kit's 48px field; sm is 40px, for an inline rename or a picker in a row of items. */
    size?: "sm" | "md";
};

const Field = ({
    className,
    classInput,
    label,
    innerLabel,
    tooltip,
    children,
    textarea,
    type,
    validated,
    handleForgotPassword,
    forgotPasswordLabel = "Forgot password?",
    description,
    error,
    variant = "secondary",
    optional,
    optionalLabel = "Optional",
    size = "md",
    id,
    ...inputProps
}: FieldProps &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> &
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">) => {
    const generatedId = useId();
    const controlId = id ?? generatedId;
    const hintId = `${controlId}-hint`;
    const errorId = `${controlId}-error`;
    const describedBy = [description && !error ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
    const text = label ?? innerLabel;
    const inputClass = `input ${variant === "secondary" ? "input--secondary" : ""} ${size === "sm" ? "input--sm" : ""} ${validated ? "pe-12" : ""} ${classInput || ""}`;
    return (
        <div className={`textfield ${className || ""}`} data-invalid={error ? true : undefined}>
            {text || handleForgotPassword ? (
                <div className="flex items-center gap-1.5">
                    {text ? <label className="textfield__label" htmlFor={controlId}>{text}</label> : null}
                    {text && optional ? <span className="textfield__optional">{optionalLabel}</span> : null}
                    {tooltip && <Tooltip content={tooltip} />}
                    {handleForgotPassword && (
                        <button
                            type="button"
                            className="ms-auto text-xs font-medium text-t-secondary transition-colors hover:text-t-primary"
                            onClick={handleForgotPassword}
                        >
                            {forgotPasswordLabel}
                        </button>
                    )}
                </div>
            ) : null}
            <div className="relative">
                {children}
                {textarea ? (
                    <textarea
                        className={inputClass}
                        id={controlId}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={describedBy}
                        {...inputProps}
                    ></textarea>
                ) : (
                    <input
                        className={inputClass}
                        type={type || "text"}
                        id={controlId}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={describedBy}
                        {...inputProps}
                    />
                )}
                {validated && (
                    <Icon
                        className="absolute top-1/2 end-4 -translate-y-1/2 fill-positive-stroke"
                        name="check"
                    />
                )}
            </div>
            {description && !error ? <div className="textfield__description" id={hintId}>{description}</div> : null}
            {error ? <div className="textfield__error" id={errorId}>{error}</div> : null}
        </div>
    );
};

export default Field;
